import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useToasts, type Toast } from '@/stores/toasts';
import { cn } from '@/lib/cn';
import { spring } from '@/lib/motion';
import { Icon } from './Icon';

const TONE: Record<Toast['tone'], { bar: string; icon: 'check' | 'x' | 'sparkle' | 'people' }> = {
  info: { bar: 'bg-bone', icon: 'sparkle' },
  success: { bar: 'bg-green', icon: 'check' },
  error: { bar: 'bg-danger', icon: 'x' },
  people: { bar: 'bg-yellow', icon: 'people' },
};

function ToastItem({ t }: { t: Toast }) {
  const dismiss = useToasts((s) => s.dismiss);
  useEffect(() => {
    const id = setTimeout(() => dismiss(t.id), t.duration);
    return () => clearTimeout(id);
  }, [t.id, t.duration, dismiss]);
  const tone = TONE[t.tone];
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8 }}
      transition={spring}
      role={t.tone === 'error' ? 'alert' : 'status'}
      className="pointer-events-auto flex w-full max-w-sm items-start gap-3 overflow-hidden rounded-card bg-primary p-3.5 pr-2 text-ground shadow-lift"
    >
      <span className={cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-night', tone.bar)}>
        <Icon name={tone.icon} size={16} strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold">{t.title}</p>
        {t.body && <p className="mt-0.5 text-sm text-ground/75">{t.body}</p>}
      </div>
      <button
        type="button"
        onClick={() => dismiss(t.id)}
        className="rounded-full p-1.5 text-ground/70 hover:text-ground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ground"
        aria-label="Dismiss notification"
      >
        <Icon name="x" size={16} />
      </button>
    </motion.div>
  );
}

export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top)+0.75rem)] z-[90] flex flex-col items-center gap-2 px-4 sm:items-end sm:pr-6" aria-live="polite">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastItem key={t.id} t={t} />
        ))}
      </AnimatePresence>
    </div>
  );
}
